import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
	Box,
	Button,
	Checkbox,
	Divider,
	FormControlLabel,
	IconButton,
	Link,
	Popover,
	Typography
} from "@mui/material";
import React, { useState } from "react";

export type ColumnDef = {
	id: string;
	label: string;
	width: number;
	alwaysVisible?: boolean;
	/** Hidden until chosen for a user with no saved column choices. Omitted means shown. */
	defaultVisible?: boolean;
	/** Added after users may have saved choices: hidden for them until they opt in. */
	isNew?: boolean;
};

export default function ColumnVisibilityPopover({
	columns,
	visibleColumns,
	onApply,
	align = "center"
}: {
	columns: ColumnDef[];
	visibleColumns: Set<string>;
	onApply: (newVisible: Set<string>) => void;
	align?: "center" | "right";
}) {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [draft, setDraft] = useState<Set<string>>(new Set());

	const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		setDraft(new Set(visibleColumns));
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => setAnchorEl(null);

	const handleToggle = (id: string) => {
		setDraft((prev) => {
			const next = new Set(prev);
			next.has(id) ? next.delete(id) : next.add(id);
			return next;
		});
	};

	const toggleableColumns = columns.filter((c) => !c.alwaysVisible);
	const allShown = toggleableColumns.every((c) => draft.has(c.id));
	const allHidden = toggleableColumns.every((c) => !draft.has(c.id));

	const handleShowAll = () =>
		setDraft((prev) => new Set([...prev, ...toggleableColumns.map((c) => c.id)]));

	const handleHideAll = () =>
		setDraft((prev) => {
			const next = new Set(prev);
			toggleableColumns.forEach((c) => next.delete(c.id));
			return next;
		});

	const handleApply = () => {
		onApply(new Set(draft));
		handleClose();
	};

	return (
		<Box sx={{ display: "flex", justifyContent: align === "right" ? "flex-end" : "center" }}>
			<IconButton size="small" onClick={handleOpen} sx={{ color: "primary.main", p: 0.5 }}>
				<MoreVertIcon fontSize="small" />
			</IconButton>
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
			>
				<Box sx={{ display: "flex", flexDirection: "column", minWidth: 200, maxHeight: 400 }}>
					<Box sx={{ px: 1.5, pt: 1.5 }}>
						<Typography
							variant="text12Uppercase"
							sx={{ color: "text.secondary", px: 0.5, mb: 0.5, display: "block" }}
						>
							Show columns
						</Typography>
						<Divider sx={{ mb: 0.5 }} />
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.5, mb: 0.5 }}>
							<Link
								component="button"
								onClick={handleShowAll}
								disabled={allShown}
								sx={{ typography: "caption", textDecoration: "none" }}
							>
								Show all
							</Link>
							<Typography
								sx={{ typography: "caption", textDecoration: "none", color: "text.disabled" }}
							>
								|
							</Typography>
							<Link
								component="button"
								onClick={handleHideAll}
								disabled={allHidden}
								sx={{ typography: "caption", textDecoration: "none" }}
							>
								Hide all
							</Link>
						</Box>
					</Box>
					<Box sx={{ overflowY: "auto", px: 1.5 }}>
						{columns.map((col) => (
							<FormControlLabel
								key={col.id}
								control={
									<Checkbox
										size="small"
										checked={draft.has(col.id) || !!col.alwaysVisible}
										disabled={col.alwaysVisible}
										onChange={() => !col.alwaysVisible && handleToggle(col.id)}
									/>
								}
								label={<Typography variant="text12">{col.label}</Typography>}
								sx={{ display: "flex", mx: 0, my: 0.25 }}
							/>
						))}
					</Box>
					<Box sx={{ px: 1.5, pb: 1.5 }}>
						<Divider sx={{ mt: 0.5, mb: 1 }} />
						<Button
							variant="contained"
							size="small"
							fullWidth
							onClick={handleApply}
							sx={{ textTransform: "none" }}
							disableElevation
						>
							Apply
						</Button>
					</Box>
				</Box>
			</Popover>
		</Box>
	);
}
